import { useState } from "react";
import {
  AppBar,
  Box,
  Button,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  Toolbar,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import { Link } from "react-router-dom";
import NgwaahiaIcon from "../../../common-components/NgwaahiaIcon";
import { frontendRoutes } from "../../../utility/frontendRoutes";
import { landingColors } from "../landingColors";
import useLandingSectionLink from "../hooks/useLandingSectionLink";

const NAV_LINKS = [
  { label: "Features", sectionId: "features" },
  { label: "FAQ", sectionId: "faq" },
];

const LandingNav = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [drawerOpen, setDrawerOpen] = useState(false);
  const sectionLink = useLandingSectionLink();

  const ctaLink = sectionLink("get-the-app");

  const closeDrawer = () => setDrawerOpen(false);

  return (
    <AppBar
      position="sticky"
      elevation={0}
      sx={{
        bgcolor: landingColors.background,
        borderBottom: `1px solid ${landingColors.border}`,
      }}
    >
      <Toolbar
        sx={{ height: 68, minHeight: "68px !important", px: { xs: 2, md: 5 } }}
      >
        <Box
          component={Link}
          to="/"
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            textDecoration: "none",
            mr: "auto",
          }}
        >
          <NgwaahiaIcon size={32} color={landingColors.primary} />
          <Typography
            fontWeight={700}
            fontSize={18}
            color={landingColors.primary}
          >
            Ngwaahia
          </Typography>
        </Box>

        {isMobile ? (
          <>
            <IconButton
              onClick={() => setDrawerOpen(true)}
              aria-label="Open menu"
            >
              <MenuIcon sx={{ color: landingColors.textPrimary }} />
            </IconButton>
            <Drawer anchor="right" open={drawerOpen} onClose={closeDrawer}>
              <Box sx={{ width: 260, py: 2 }}>
                <List>
                  {NAV_LINKS.map((link) => (
                    <ListItemButton
                      key={link.label}
                      {...sectionLink(link.sectionId)}
                      onClick={closeDrawer}
                    >
                      <ListItemText primary={link.label} />
                    </ListItemButton>
                  ))}
                  <ListItemButton
                    component={Link}
                    to={frontendRoutes.HELP}
                    onClick={closeDrawer}
                  >
                    <ListItemText primary="Help" />
                  </ListItemButton>
                  <ListItemButton
                    component={Link}
                    to={frontendRoutes.HOME}
                    onClick={closeDrawer}
                  >
                    <ListItemText
                      primary="Browse Marketplace"
                      primaryTypographyProps={{ color: landingColors.primary }}
                    />
                  </ListItemButton>
                </List>
                <Box sx={{ px: 2, pt: 1 }}>
                  <Button
                    {...ctaLink}
                    onClick={closeDrawer}
                    fullWidth
                    variant="contained"
                    sx={{
                      bgcolor: landingColors.primary,
                      "&:hover": { bgcolor: landingColors.accent },
                      borderRadius: 3,
                      fontWeight: 700,
                    }}
                  >
                    Get the App
                  </Button>
                </Box>
              </Box>
            </Drawer>
          </>
        ) : (
          <Box sx={{ display: "flex", alignItems: "center", gap: 4 }}>
            {NAV_LINKS.map((link) => (
              <Typography
                key={link.label}
                {...sectionLink(link.sectionId)}
                sx={{
                  color: landingColors.textPrimary,
                  fontWeight: 500,
                  textDecoration: "none",
                  "&:hover": { color: landingColors.primary },
                }}
              >
                {link.label}
              </Typography>
            ))}
            <Typography
              component={Link}
              to={frontendRoutes.HELP}
              sx={{
                color: landingColors.textPrimary,
                fontWeight: 500,
                textDecoration: "none",
                "&:hover": { color: landingColors.primary },
              }}
            >
              Help
            </Typography>
            <Button
              component={Link}
              to={frontendRoutes.HOME}
              sx={{ color: landingColors.primary, fontWeight: 500 }}
            >
              Browse Marketplace
            </Button>
            <Button
              {...ctaLink}
              variant="contained"
              sx={{
                bgcolor: landingColors.primary,
                "&:hover": { bgcolor: landingColors.accent },
                borderRadius: 3,
                fontWeight: 700,
                px: 3,
              }}
            >
              Get the App
            </Button>
          </Box>
        )}
      </Toolbar>
    </AppBar>
  );
};

export default LandingNav;
